import { GENERATED_QUESTIONS } from './questionBankGenerated';
// ── Question Bank ──────────────────────────────────────────────────
// Phase A seed: ~30 example questions covering key exam × year × primitive combos.
// Real content generation happens in Phase C.

export type QuestionBankEntry = {
  id: string;
  examProgram: 'naplan' | 'selective_nsw' | 'oc' | 'acer' | 'icas' | 'hsc' | 'vce';
  yearId: 'y3' | 'y4' | 'y5' | 'y6' | 'y7' | 'y8' | 'y9' | 'y10' | 'y11' | 'y12';
  subject: 'reading' | 'numeracy' | 'writing' | 'language' | 'thinking' | 'science' | 'humanities' | 'english' | 'maths'
    | 'english_advanced' | 'mathematics_advanced' | 'physics' | 'chemistry' | 'biology'
    | 'modern_history' | 'legal_studies' | 'economics' | 'mathematical_methods'
    | 'further_mathematics' | 'australian_history' | 'abstract_reasoning';
  primitiveType: 'DragDrop' | 'InlineCloze' | 'HotSpot' | 'HotText2' | 'ExtractMatching' | 'GapMatch' | 'ImageMCQ' | 'ExamMode';
  config: any; // primitive-specific config object
  difficulty?: 'easy' | 'medium' | 'hard';
  source?: string;
};

// ── SVG helpers for seed data ──────────────────────────────────────

const triangle = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 90" width="80" height="72">
  <polygon points="50,8 92,82 8,82" fill="#60A5FA" stroke="#1D4ED8" stroke-width="2"/>
</svg>`;

const square = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80">
  <rect x="10" y="10" width="80" height="80" fill="#34D399" stroke="#059669" stroke-width="2"/>
</svg>`;

const circle = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80">
  <circle cx="50" cy="50" r="40" fill="#F87171" stroke="#DC2626" stroke-width="2"/>
</svg>`;

const pentagon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="80" height="80">
  <polygon points="50,5 95,38 78,90 22,90 5,38" fill="#FBBF24" stroke="#D97706" stroke-width="2"/>
</svg>`;

// Pattern completion stimuli
const patternStimulus = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 300" width="280" height="280">
  <rect width="300" height="300" fill="#F9FAFB" rx="8"/>
  <!-- Grid lines -->
  <line x1="100" y1="0" x2="100" y2="300" stroke="#E5E7EB" stroke-width="1.5"/>
  <line x1="200" y1="0" x2="200" y2="300" stroke="#E5E7EB" stroke-width="1.5"/>
  <line x1="0" y1="100" x2="300" y2="100" stroke="#E5E7EB" stroke-width="1.5"/>
  <line x1="0" y1="200" x2="300" y2="200" stroke="#E5E7EB" stroke-width="1.5"/>
  <!-- Row 1: 1 dot, 2 dots, 3 dots -->
  <circle cx="50" cy="50" r="14" fill="#6366F1"/>
  <circle cx="150" cy="40" r="12" fill="#6366F1"/> <circle cx="150" cy="62" r="12" fill="#6366F1"/>
  <circle cx="220" cy="34" r="10" fill="#6366F1"/> <circle cx="250" cy="50" r="10" fill="#6366F1"/> <circle cx="220" cy="66" r="10" fill="#6366F1"/>
  <!-- Row 2: small square, medium square, large square -->
  <rect x="38" y="138" width="24" height="24" fill="#F59E0B"/>
  <rect x="128" y="128" width="44" height="44" fill="#F59E0B"/>
  <rect x="210" y="112" width="68" height="68" rx="2" fill="#F59E0B"/>
  <!-- Row 3: triangle pointing up, down, up+down -->
  <polygon points="50,260 30,290 70,290" fill="#10B981"/>
  <polygon points="150,290 130,260 170,260" fill="#10B981"/>
  <!-- Bottom right: question mark -->
  <rect x="200" y="202" width="98" height="96" fill="#EDE9FE" rx="4" stroke="#7C3AED" stroke-width="1.5" stroke-dasharray="4 2"/>
  <text x="249" y="256" font-size="36" text-anchor="middle" fill="#7C3AED" font-weight="bold">?</text>
</svg>`;

const patternAnswerA = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="70" height="70">
  <polygon points="40,8 10,72 70,72" fill="#10B981"/>
  <polygon points="40,72 10,8 70,8" fill="#10B981"/>
</svg>`;
const patternAnswerB = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="70" height="70">
  <circle cx="40" cy="40" r="30" fill="#10B981"/>
</svg>`;
const patternAnswerC = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="70" height="70">
  <polygon points="40,5 5,75 75,75" fill="#10B981"/>
</svg>`;
const patternAnswerD = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="70" height="70">
  <rect x="8" y="8" width="64" height="64" fill="#10B981"/>
</svg>`;

// Simple bar charts for Selective Thinking Skills
const chartUpward = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 90" width="110" height="80">
  <rect x="5" y="70" width="18" height="15" fill="#60A5FA"/><rect x="30" y="52" width="18" height="33" fill="#60A5FA"/>
  <rect x="55" y="34" width="18" height="51" fill="#60A5FA"/><rect x="80" y="16" width="18" height="69" fill="#60A5FA"/>
  <line x1="5" y1="85" x2="105" y2="85" stroke="#374151" stroke-width="1.5"/>
  <line x1="5" y1="5" x2="5" y2="85" stroke="#374151" stroke-width="1.5"/>
  <text x="60" y="10" font-size="9" text-anchor="middle" fill="#374151">↑ sales</text>
</svg>`;
const chartDownward = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 90" width="110" height="80">
  <rect x="5" y="16" width="18" height="69" fill="#F87171"/><rect x="30" y="34" width="18" height="51" fill="#F87171"/>
  <rect x="55" y="52" width="18" height="33" fill="#F87171"/><rect x="80" y="70" width="18" height="15" fill="#F87171"/>
  <line x1="5" y1="85" x2="105" y2="85" stroke="#374151" stroke-width="1.5"/>
  <line x1="5" y1="5" x2="5" y2="85" stroke="#374151" stroke-width="1.5"/>
</svg>`;
const chartFlat = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 90" width="110" height="80">
  <rect x="5" y="38" width="18" height="47" fill="#A3A3A3"/><rect x="30" y="36" width="18" height="49" fill="#A3A3A3"/>
  <rect x="55" y="40" width="18" height="45" fill="#A3A3A3"/><rect x="80" y="37" width="18" height="48" fill="#A3A3A3"/>
  <line x1="5" y1="85" x2="105" y2="85" stroke="#374151" stroke-width="1.5"/>
  <line x1="5" y1="5" x2="5" y2="85" stroke="#374151" stroke-width="1.5"/>
</svg>`;
const chartVShape = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 90" width="110" height="80">
  <rect x="5" y="20" width="18" height="65" fill="#FBBF24"/><rect x="30" y="50" width="18" height="35" fill="#FBBF24"/>
  <rect x="55" y="65" width="18" height="20" fill="#FBBF24"/><rect x="80" y="30" width="18" height="55" fill="#FBBF24"/>
  <line x1="5" y1="85" x2="105" y2="85" stroke="#374151" stroke-width="1.5"/>
  <line x1="5" y1="5" x2="5" y2="85" stroke="#374151" stroke-width="1.5"/>
</svg>`;

// ── QUESTION BANK ──────────────────────────────────────────────────

const SEED_QUESTIONS: QuestionBankEntry[] = [

  // ── NAPLAN Y3 ────────────────────────────────────────────────────

  {
    id: 'naplan_y3_num_imcq_001',
    examProgram: 'naplan', yearId: 'y3', subject: 'numeracy',
    primitiveType: 'ImageMCQ', difficulty: 'easy',
    source: 'ACARA NAPLAN 2024 Numeracy sample',
    config: {
      prompt: 'Which shape below is a triangle?',
      options: [
        { id: 'a', label: 'A', content: triangle, contentType: 'svg', isCorrect: true },
        { id: 'b', label: 'B', content: square,   contentType: 'svg', isCorrect: false },
        { id: 'c', label: 'C', content: circle,   contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: pentagon, contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'A triangle has exactly 3 straight sides and 3 angles. Option A is the only shape with 3 sides.',
    },
  },

  {
    id: 'naplan_y3_read_dragdrop_001',
    examProgram: 'naplan', yearId: 'y3', subject: 'reading',
    primitiveType: 'DragDrop', difficulty: 'easy',
    source: 'NAPLAN 2024 Reading sample',
    config: {
      pattern: 'order',
      prompt: 'Drag the events into the order they happened in the story.',
      items: [
        { id: 'i1', content: 'Mia found a lost puppy in the rain.' },
        { id: 'i2', content: 'She knocked on every door in the street.' },
        { id: 'i3', content: 'The puppy\'s owner came running and hugged Mia.' },
        { id: 'i4', content: 'A neighbour told her which house the puppy belonged to.' },
      ],
      correctOrder: ['i1', 'i2', 'i4', 'i3'],
      explanation: 'First Mia found the puppy, then she knocked on doors, then a neighbour helped, and finally the owner arrived.',
    },
  },

  {
    id: 'naplan_y3_lang_hottext_001',
    examProgram: 'naplan', yearId: 'y3', subject: 'language',
    primitiveType: 'HotText2', difficulty: 'easy',
    source: 'NAPLAN 2024 Language sample',
    config: {
      prompt: 'Click on the word that is spelled incorrectly.',
      passage: 'The childrens went to the libary after skool to read books.',
      granularity: 'word',
      selectionMode: 'multi',
      correctSelections: ['libary', 'skool'],
      explanation: 'The correct spellings are "library" and "school". "Childrens" is also incorrect — children is already plural.',
    },
  },

  {
    id: 'naplan_y3_num_hotspot_001',
    examProgram: 'naplan', yearId: 'y3', subject: 'numeracy',
    primitiveType: 'HotSpot', difficulty: 'easy',
    source: 'NAPLAN 2024 Numeracy sample',
    config: {
      prompt: 'Click on the point that shows the number 4½ on the number line.',
      image: {
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 120" width="500" height="120">
  <rect width="500" height="120" fill="#FAFAF6"/>
  <line x1="30" y1="60" x2="470" y2="60" stroke="#374151" stroke-width="3"/>
  ${[0,1,2,3,4,5,6,7,8].map((n,i) => `
    <line x1="${30+i*55}" y1="48" x2="${30+i*55}" y2="72" stroke="#374151" stroke-width="2"/>
    <text x="${30+i*55}" y="90" font-size="18" text-anchor="middle" fill="#374151" font-family="sans-serif">${n}</text>
  `).join('')}
  <line x1="${30+4*55+27}" y1="52" x2="${30+4*55+27}" y2="68" stroke="#9CA3AF" stroke-width="1.5"/>
  <circle cx="${30+0*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+1*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+2*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+3*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+4*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+4*55+27}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+5*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+6*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+7*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
  <circle cx="${30+8*55}" cy="60" r="8" fill="white" stroke="#374151" stroke-width="2"/>
</svg>`)}`,
        alt: 'Number line from 0 to 8 with dots at each whole number and the halfway point between 4 and 5',
        width: 500,
        height: 120,
      },
      zones: [
        { id: 'z0', type: 'circle', cx: 30,  cy: 60, r: 16, label: '0' },
        { id: 'z1', type: 'circle', cx: 85,  cy: 60, r: 16, label: '1' },
        { id: 'z2', type: 'circle', cx: 140, cy: 60, r: 16, label: '2' },
        { id: 'z3', type: 'circle', cx: 195, cy: 60, r: 16, label: '3' },
        { id: 'z4', type: 'circle', cx: 250, cy: 60, r: 16, label: '4' },
        { id: 'z4h', type: 'circle', cx: 277, cy: 60, r: 16, label: '4½', isCorrect: true },
        { id: 'z5', type: 'circle', cx: 305, cy: 60, r: 16, label: '5' },
        { id: 'z6', type: 'circle', cx: 360, cy: 60, r: 16, label: '6' },
        { id: 'z7', type: 'circle', cx: 415, cy: 60, r: 16, label: '7' },
        { id: 'z8', type: 'circle', cx: 470, cy: 60, r: 16, label: '8' },
      ],
      selectionMode: 'single',
      explanation: '4½ is halfway between 4 and 5. It is marked by the small tick between the 4 and 5 dots.',
    },
  },

  // ── NAPLAN Y5 ────────────────────────────────────────────────────

  {
    id: 'naplan_y5_num_imcq_001',
    examProgram: 'naplan', yearId: 'y5', subject: 'numeracy',
    primitiveType: 'ImageMCQ', difficulty: 'medium',
    source: 'ACARA NAPLAN 2024 Numeracy Y5 sample',
    config: {
      prompt: 'Which figure correctly completes the pattern in the bottom-right cell?',
      stimulus: {
        type: 'svg',
        content: patternStimulus,
        alt: 'A 3×3 grid showing a pattern of dots (row 1), squares (row 2), and triangles (row 3). The bottom-right cell is empty with a question mark.',
        caption: 'Each row follows a pattern. Find what belongs in the bottom-right cell.',
      },
      options: [
        { id: 'a', label: 'A', content: patternAnswerA, contentType: 'svg', isCorrect: true },
        { id: 'b', label: 'B', content: patternAnswerB, contentType: 'svg', isCorrect: false },
        { id: 'c', label: 'C', content: patternAnswerC, contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: patternAnswerD, contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'Row 3 shows triangles. Position 1 has a triangle pointing up, position 2 has one pointing down — so position 3 has both pointing up and down together. Option A shows this correctly.',
    },
  },

  {
    id: 'naplan_y5_read_dragdrop_001',
    examProgram: 'naplan', yearId: 'y5', subject: 'reading',
    primitiveType: 'DragDrop', difficulty: 'medium',
    source: 'NAPLAN 2024 Reading Y5',
    config: {
      pattern: 'gap_fill',
      prompt: 'Drag each word into the correct gap in the passage.',
      items: [
        { id: 'w1', content: 'however' },
        { id: 'w2', content: 'therefore' },
        { id: 'w3', content: 'although' },
        { id: 'w4', content: 'because' },
      ],
      gaps: [
        { id: 'g1', sentenceFragment: 'The trail looked safe,', correctItemId: 'w1' },
        { id: 'g2', sentenceFragment: 'there was still ice on the path.', correctItemId: 'w3' },
        { id: 'g3', sentenceFragment: 'They brought extra torches', correctItemId: 'w4' },
        { id: 'g4', sentenceFragment: 'they expected to return after dark.', correctItemId: 'w2' },
      ],
      explanation: '"However" and "although" signal contrast. "Because" signals cause. "Therefore" signals consequence.',
    },
  },

  {
    id: 'naplan_y5_lang_hottext_001',
    examProgram: 'naplan', yearId: 'y5', subject: 'language',
    primitiveType: 'HotText2', difficulty: 'medium',
    source: 'NAPLAN 2024 Language Y5',
    config: {
      prompt: 'Click on the sentence that contains a grammatical error.',
      passage: 'The scientists discovered a new species of fish deep in the ocean. They was surprised by how colourful it was. The discovery was announced at a conference last week. Many researchers attended the presentation.',
      granularity: 'sentence',
      selectionMode: 'single',
      correctSelections: ['They was surprised by how colourful it was.'],
      explanation: '"They was" is incorrect. It should be "They were" — "was" is used with singular subjects (I, he, she, it), while "were" is used with plural subjects like "they".',
    },
  },

  // ── NSW Selective Y6 ─────────────────────────────────────────────

  {
    id: 'sel_y6_read_inline_001',
    examProgram: 'selective_nsw', yearId: 'y6', subject: 'reading',
    primitiveType: 'InlineCloze', difficulty: 'medium',
    source: 'NSW Selective 2026 sample',
    config: {
      prompt: 'Choose the best word for each blank.',
      passage: 'The explorers {{1}} the dense jungle for three weeks before {{2}} a river that would guide them to the coast. Their supplies were {{3}}, but their determination remained unbroken.',
      blanks: [
        { id: '1', options: ['traversed', 'skipped', 'watched', 'painted'], correctId: 'traversed' },
        { id: '2', options: ['locating', 'losing', 'painting', 'ignoring'], correctId: 'locating' },
        { id: '3', options: ['depleted', 'doubled', 'ignored', 'celebrated'], correctId: 'depleted' },
      ],
      explanation: '"Traversed" means crossed/travelled through. "Locating" means finding. "Depleted" means nearly used up.',
    },
  },

  {
    id: 'sel_y6_think_imcq_001',
    examProgram: 'selective_nsw', yearId: 'y6', subject: 'thinking',
    primitiveType: 'ImageMCQ', difficulty: 'hard',
    source: 'NSW Selective Thinking Skills 2026',
    config: {
      prompt: 'A survey found that as the number of hours students study per week increases, their test scores also increase. Which graph best represents this relationship?',
      stimulus: {
        type: 'text',
        content: 'Study hours (x-axis) vs Test score (y-axis). The survey covered 200 students in Years 5–6.',
        alt: 'Text description of study hours vs test score survey',
      },
      options: [
        { id: 'a', label: 'A', content: chartUpward,  contentType: 'svg', isCorrect: true },
        { id: 'b', label: 'B', content: chartDownward, contentType: 'svg', isCorrect: false },
        { id: 'c', label: 'C', content: chartFlat,    contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: chartVShape,  contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'When both variables increase together, the graph shows bars (or a line) going upward from left to right. Option A shows this positive relationship correctly.',
    },
  },

  {
    id: 'sel_y6_read_extract_001',
    examProgram: 'selective_nsw', yearId: 'y6', subject: 'reading',
    primitiveType: 'ExtractMatching', difficulty: 'hard',
    source: 'NSW Selective 2026 sample',
    config: {
      prompt: 'Match each quote from the extract to the literary technique it demonstrates.',
      stimulus: {
        text: '"The city breathed at night, its streets alive with whispers." / "Like a thousand clocks, the rain tapped on every roof." / "She was fire and ice in a single glance." / "He ran and ran and ran until the world became a blur."',
      },
      questions: [
        {
          id: 'q1',
          text: 'The city breathed at night, its streets alive with whispers.',
          options: ['Personification', 'Simile', 'Metaphor', 'Repetition'],
          correctIndex: 0,
        },
        {
          id: 'q2',
          text: 'Like a thousand clocks, the rain tapped on every roof.',
          options: ['Personification', 'Simile', 'Metaphor', 'Repetition'],
          correctIndex: 1,
        },
        {
          id: 'q3',
          text: 'She was fire and ice in a single glance.',
          options: ['Personification', 'Simile', 'Metaphor', 'Repetition'],
          correctIndex: 2,
        },
        {
          id: 'q4',
          text: 'He ran and ran and ran until the world became a blur.',
          options: ['Personification', 'Simile', 'Metaphor', 'Repetition'],
          correctIndex: 3,
        },
      ],
      explanation: 'Personification gives human traits to non-human things. Simile uses "like" or "as". Metaphor directly states something is something else. Repetition repeats words for emphasis.',
    },
  },

  // ── OC Y4 ────────────────────────────────────────────────────────

  {
    id: 'oc_y4_read_inline_001',
    examProgram: 'oc', yearId: 'y4', subject: 'reading',
    primitiveType: 'InlineCloze', difficulty: 'easy',
    source: 'OC Test 2026 sample',
    config: {
      prompt: 'Choose the best word for each gap.',
      passage: 'The young kangaroo, called a {{1}}, stays in its mother\'s pouch for several months. This is because joeys are born very {{2}} and need protection while they continue to {{3}}.',
      blanks: [
        { id: '1', options: ['joey', 'pup', 'foal', 'cub'], correctId: 'joey' },
        { id: '2', options: ['underdeveloped', 'large', 'hungry', 'fast'], correctId: 'underdeveloped' },
        { id: '3', options: ['develop', 'sleep', 'eat', 'play'], correctId: 'develop' },
      ],
      explanation: 'A baby kangaroo is called a "joey". They are born underdeveloped and use the pouch to continue developing.',
    },
  },

  {
    id: 'oc_y4_num_dragdrop_001',
    examProgram: 'oc', yearId: 'y4', subject: 'numeracy',
    primitiveType: 'DragDrop', difficulty: 'easy',
    source: 'OC Test 2026 sample',
    config: {
      pattern: 'categorise',
      prompt: 'Sort these numbers into the correct category.',
      items: [
        { id: 'n1', content: '36' },
        { id: 'n2', content: '15' },
        { id: 'n3', content: '48' },
        { id: 'n4', content: '25' },
        { id: 'n5', content: '100' },
        { id: 'n6', content: '33' },
      ],
      buckets: [
        { id: 'b1', label: 'Perfect Squares', correctItemIds: ['n1', 'n4', 'n5'] },
        { id: 'b2', label: 'Not Perfect Squares', correctItemIds: ['n2', 'n3', 'n6'] },
      ],
      explanation: 'Perfect squares: 36 = 6², 25 = 5², 100 = 10². The others (15, 48, 33) cannot be expressed as an integer squared.',
    },
  },

  {
    id: 'oc_y4_think_imcq_001',
    examProgram: 'oc', yearId: 'y4', subject: 'thinking',
    primitiveType: 'ImageMCQ', difficulty: 'medium',
    source: 'OC Test Thinking Skills 2026',
    config: {
      prompt: 'Which figure correctly completes the pattern?',
      stimulus: {
        type: 'svg',
        content: patternStimulus,
        alt: '3×3 grid with patterns in the first two rows and a question mark in the bottom right.',
      },
      options: [
        { id: 'a', label: 'A', content: patternAnswerA, contentType: 'svg', isCorrect: true },
        { id: 'b', label: 'B', content: patternAnswerB, contentType: 'svg', isCorrect: false },
        { id: 'c', label: 'C', content: patternAnswerC, contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: patternAnswerD, contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'Each row follows a consistent pattern. Row 3 uses triangles — position 3 combines both directions from the previous positions.',
    },
  },

  // ── ACER Y7 ─────────────────────────────────────────────────────

  {
    id: 'acer_y7_think_imcq_001',
    examProgram: 'acer', yearId: 'y7', subject: 'thinking',
    primitiveType: 'ImageMCQ', difficulty: 'medium',
    source: 'ACER Scholarship 2025 Abstract Reasoning sample',
    config: {
      prompt: 'Which option completes the pattern in the grid?',
      stimulus: {
        type: 'svg',
        content: patternStimulus,
        alt: '3×3 abstract reasoning grid with one cell missing.',
        caption: 'Identify the rule and select the shape that belongs in the empty cell.',
      },
      options: [
        { id: 'a', label: 'A', content: patternAnswerA, contentType: 'svg', isCorrect: true },
        { id: 'b', label: 'B', content: patternAnswerB, contentType: 'svg', isCorrect: false },
        { id: 'c', label: 'C', content: patternAnswerC, contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: patternAnswerD, contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'Look for patterns across rows and columns. Each shape type and orientation follows a rule — apply it to find the missing piece.',
    },
  },

  {
    id: 'acer_y7_think_imcq_002',
    examProgram: 'acer', yearId: 'y7', subject: 'thinking',
    primitiveType: 'ImageMCQ', difficulty: 'hard',
    source: 'ACER Scholarship 2025 Abstract Reasoning sample',
    config: {
      prompt: 'If the pattern in the grid continues, which option belongs in position marked "?"',
      stimulus: {
        type: 'svg',
        content: patternStimulus,
        alt: 'Abstract reasoning grid',
      },
      options: [
        { id: 'a', label: 'A', content: patternAnswerC, contentType: 'svg', isCorrect: false },
        { id: 'b', label: 'B', content: patternAnswerA, contentType: 'svg', isCorrect: true },
        { id: 'c', label: 'C', content: patternAnswerD, contentType: 'svg', isCorrect: false },
        { id: 'd', label: 'D', content: patternAnswerB, contentType: 'svg', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'The pattern across rows determines both the shape type and the transformation rule. Apply both rules to identify the correct answer.',
    },
  },

  // ── ICAS Y5 ──────────────────────────────────────────────────────

  {
    id: 'icas_y5_maths_gapmatch_001',
    examProgram: 'icas', yearId: 'y5', subject: 'maths',
    primitiveType: 'GapMatch', difficulty: 'medium',
    source: 'ICAS Mathematics 2024 sample',
    config: {
      prompt: 'Match each expression on the left to its equal value on the right.',
      leftItems: [
        { id: 'l1', content: '4² + 2³' },
        { id: 'l2', content: '√144' },
        { id: 'l3', content: '3 × (8 + 2)' },
        { id: 'l4', content: '100 ÷ 4 – 5' },
      ],
      rightItems: [
        { id: 'r1', content: '24', correctLeftId: 'l1' },
        { id: 'r2', content: '12', correctLeftId: 'l2' },
        { id: 'r3', content: '30', correctLeftId: 'l3' },
        { id: 'r4', content: '20', correctLeftId: 'l4' },
      ],
      explanation: '4²+2³=16+8=24 | √144=12 | 3×10=30 | 100÷4−5=25−5=20',
    },
  },

  {
    id: 'icas_y5_science_imcq_001',
    examProgram: 'icas', yearId: 'y5', subject: 'science',
    primitiveType: 'ImageMCQ', difficulty: 'medium',
    source: 'ICAS Science 2024 Y5 sample',
    config: {
      prompt: 'Which option correctly shows the life cycle of a butterfly in the right order?',
      options: [
        { id: 'a', label: 'A', content: 'Egg → Caterpillar (larva) → Pupa (chrysalis) → Butterfly', contentType: 'text', isCorrect: true },
        { id: 'b', label: 'B', content: 'Egg → Butterfly → Caterpillar → Pupa', contentType: 'text', isCorrect: false },
        { id: 'c', label: 'C', content: 'Caterpillar → Egg → Pupa → Butterfly', contentType: 'text', isCorrect: false },
        { id: 'd', label: 'D', content: 'Pupa → Egg → Caterpillar → Butterfly', contentType: 'text', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'Metamorphosis: Egg hatches into a caterpillar (larva). The caterpillar forms a pupa (chrysalis). Inside the pupa, it transforms into a butterfly.',
    },
  },

  // ── HSC Y12 ──────────────────────────────────────────────────────

  {
    id: 'hsc_y12_english_exammode_001',
    examProgram: 'hsc', yearId: 'y12', subject: 'english',
    primitiveType: 'ExamMode', difficulty: 'hard',
    source: 'HSC English Advanced 2024 Paper 1 Section II',
    config: {
      exam: 'HSC',
      subject: 'English Advanced Paper 1',
      year: 2024,
      totalDurationMinutes: 40,
      sections: [
        {
          id: 's1',
          title: 'Section II — Creative Response',
          instructions: 'Write a piece of imaginative writing in response to ONE of the stimuli below. Your response will be marked for its use of language and its engagement with the stimulus. Write between 600 and 1,200 words.',
          recommendedMinutes: 40,
          questions: [
            {
              id: 'hsc_q1',
              marks: 20,
              stimulusType: 'quote',
              stimulus: '"The past is never dead. It\'s not even past."\n— William Faulkner\n\nUsing this quote as a starting point, write a piece of imaginative writing in which you explore the relationship between memory and identity.',
              prompt: 'You may write in any form — narrative, monologue, prose poem, or letter.',
              responseType: 'essay',
              minWords: 600,
              maxWords: 1200,
              markingCriteria: [
                'Sophisticated use of language forms, features and structures appropriate to purpose and context',
                'Sustained and meaningful engagement with the stimulus',
                'Effective use of literary and structural techniques',
                'Originality — a distinctive voice',
                'Accuracy of expression throughout',
              ],
            },
          ],
        },
      ],
    },
  },

  // ── VCE Y12 ──────────────────────────────────────────────────────

  {
    id: 'vce_y12_maths_exammode_001',
    examProgram: 'vce', yearId: 'y12', subject: 'maths',
    primitiveType: 'ExamMode', difficulty: 'hard',
    source: 'VCE Further Mathematics 2024 Extended Response',
    config: {
      exam: 'VCE',
      subject: 'Further Mathematics — Data Analysis',
      year: 2024,
      totalDurationMinutes: 25,
      sections: [
        {
          id: 'data1',
          title: 'Extended Response — Data Analysis',
          instructions: 'Answer all questions. Show all working. Correct answers without working may not receive full marks.',
          recommendedMinutes: 25,
          questions: [
            {
              id: 'vce_q1',
              marks: 5,
              stimulusType: 'text',
              stimulus: 'A dataset records the heights (cm) of 10 students:\n120, 135, 142, 128, 155, 148, 132, 160, 138, 145',
              prompt: 'a) Find the mean, median and range.\nb) A student of height 185 cm joins the group. Determine if this is an outlier using the 1.5 × IQR rule. Show all working.',
              responseType: 'working_out',
              markingCriteria: [
                'Mean = (120+135+142+128+155+148+132+160+138+145)/10 = 1403/10 = 140.3 cm',
                'Ordered: 120,128,132,135,138,142,145,148,155,160 → Median = (138+142)/2 = 140',
                'Range = 160 − 120 = 40',
                'Q1 = 132, Q3 = 148, IQR = 16; Upper fence = 148 + 1.5(16) = 172; 185 > 172 → outlier',
              ],
            },
          ],
        },
      ],
    },
  },

  // ── Additional seed questions (NAPLAN Y7, Y9) ─────────────────────

  {
    id: 'naplan_y7_read_hottext_001',
    examProgram: 'naplan', yearId: 'y7', subject: 'reading',
    primitiveType: 'HotText2', difficulty: 'medium',
    source: 'NAPLAN 2024 Reading Y7',
    config: {
      prompt: 'Click on the sentence that most strongly suggests the narrator feels out of place.',
      passage: 'The ballroom glittered with chandeliers and the laughter of strangers. Maya adjusted her dress for the fourth time. Everyone here seemed to know exactly how to hold a champagne flute and whom to talk to. She found a corner near the window and watched the city below — the ordinary streets she actually understood.',
      granularity: 'sentence',
      selectionMode: 'single',
      correctSelections: ['She found a corner near the window and watched the city below — the ordinary streets she actually understood.'],
      explanation: 'The phrase "the ordinary streets she actually understood" contrasts the familiar city with the unfamiliar ballroom, most directly showing she feels out of place here.',
    },
  },

  {
    id: 'naplan_y9_lang_hottext_001',
    examProgram: 'naplan', yearId: 'y9', subject: 'language',
    primitiveType: 'HotText2', difficulty: 'hard',
    source: 'NAPLAN 2024 Language Y9',
    config: {
      prompt: 'Click on the word that has the most NEGATIVE connotation in the context of this sentence.',
      passage: 'The politician\'s speech was crafted, calculated, and carefully timed to coincide with the election announcement.',
      granularity: 'word',
      selectionMode: 'single',
      correctSelections: ['calculated'],
      explanation: '"Calculated" implies cold, manipulative intent. "Crafted" and "carefully" are more neutral. In political context, "calculated" carries the strongest negative connotation of deliberate manipulation.',
    },
  },

  {
    id: 'sel_y6_maths_imcq_001',
    examProgram: 'selective_nsw', yearId: 'y6', subject: 'maths',
    primitiveType: 'ImageMCQ', difficulty: 'medium',
    source: 'NSW Selective 2026 Maths sample',
    config: {
      prompt: 'A recipe uses sugar and flour in the ratio 2:5. If you use 8 cups of sugar, how many cups of flour do you need?',
      options: [
        { id: 'a', label: 'A', content: '15 cups', contentType: 'text', isCorrect: false },
        { id: 'b', label: 'B', content: '20 cups', contentType: 'text', isCorrect: true },
        { id: 'c', label: 'C', content: '16 cups', contentType: 'text', isCorrect: false },
        { id: 'd', label: 'D', content: '25 cups', contentType: 'text', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: 'Ratio 2:5 means for every 2 cups of sugar, use 5 cups of flour. 8 cups of sugar = 4 × 2, so flour = 4 × 5 = 20 cups.',
    },
  },

  {
    id: 'oc_y4_read_imcq_001',
    examProgram: 'oc', yearId: 'y4', subject: 'reading',
    primitiveType: 'ImageMCQ', difficulty: 'easy',
    source: 'OC Test Reading 2026',
    config: {
      prompt: 'Read the short passage and choose the best answer.\n\n"Emily had waited all year for this moment. As the curtain rose, she took a deep breath, stepped into the spotlight, and forgot every worry she had ever had."\n\nHow does Emily feel as the curtain rises?',
      options: [
        { id: 'a', label: 'A', content: 'Nervous, then relieved and focused', contentType: 'text', isCorrect: true },
        { id: 'b', label: 'B', content: 'Excited, then disappointed', contentType: 'text', isCorrect: false },
        { id: 'c', label: 'C', content: 'Bored and distracted', contentType: 'text', isCorrect: false },
        { id: 'd', label: 'D', content: 'Confident from the start', contentType: 'text', isCorrect: false },
      ],
      selectionMode: 'single',
      explanation: '"Took a deep breath" shows initial nerves. "Forgot every worry" shows she became fully focused and calm. She was nervous, then relieved — option A.',
    },
  },
];

export const QUESTION_BANK: QuestionBankEntry[] = [
  ...SEED_QUESTIONS,
  ...(GENERATED_QUESTIONS as QuestionBankEntry[]),
];
